/**
 * Customer Display Utilities
 * Centralized functions for consistent customer name display across all components
 */

/**
 * Get customer display name with comprehensive data source resolution
 * Follows banking industry standards for customer identification
 * @param {Object} primaryData - Primary customer/transaction/invoice object
 * @param {Object} customersMap - Optional customers lookup map
 * @returns {string} Display name for customer
 */
export const getCustomerDisplayName = (primaryData, customersMap = null) => {
  if (!primaryData) return 'Guest';
  
  // Phase 3 backend provides resolved customer data in transactions
  // Priority order matches banking industry standards:
  // 1. Company name (primary business identifier)
  // 2. Individual name (personal accounts)
  // 3. Email (fallback identifier)
  // 4. Customers map lookup (legacy data)
  // 5. Guest (unidentified)
  
  // Helper function to extract customer display from any data object
  const extractDisplayName = (data) => {
    if (!data) return null;
    
    // Check for company name (various field names supported)
    const companyName = data.customerCompany || 
                       data.customer_company || 
                       data.company;
    if (companyName && companyName.trim()) {
      return companyName.trim();
    }
    
    // Check for individual name (first + last)
    const firstName = data.customerFirstName || 
                     data.customer_first_name || 
                     data.firstName || 
                     data.first_name || '';
    const lastName = data.customerLastName || 
                    data.customer_last_name || 
                    data.lastName || 
                    data.last_name || '';
    
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) {
      return fullName;
    }
    
    // Check for email
    const email = data.email || data.customerEmail;
    if (email && email.trim()) {
      return email.trim();
    }
    
    return null;
  };
  
  // Try primary data first
  let displayName = extractDisplayName(primaryData);
  if (displayName) return displayName;
  
  // Try customers map lookup if available
  if (customersMap && primaryData.customerId && customersMap[primaryData.customerId]) {
    displayName = extractDisplayName(customersMap[primaryData.customerId]);
    if (displayName) return displayName;
  }
  
  // Final fallbacks for specific data types
  if (primaryData.customerEmail) return primaryData.customerEmail;
  
  return 'Guest';
};

/**
 * Format customer ID for display or API usage
 * @param {string|Object} idOrCustomer - Customer ID string or customer object
 * @returns {string|null} Formatted customer ID
 */
export const formatCustomerId = (idOrCustomer) => {
  if (!idOrCustomer) return null;
  
  // If it's an object, extract the ID
  let id = idOrCustomer;
  if (typeof idOrCustomer === 'object') {
    id = idOrCustomer.customerId || 
         idOrCustomer.customer_id || 
         idOrCustomer.id || 
         idOrCustomer.publicId || 
         idOrCustomer.public_id;
  }
  
  if (!id) return null;
  
  // Handle the cust- prefix
  return id.startsWith('cust-') ? id : `cust-${id}`;
};

/**
 * Get customer initials for avatar displays
 * @param {Object} customer - Customer object
 * @returns {string} Customer initials (1-2 characters)
 */
export const getCustomerInitials = (customer) => {
  const name = getCustomerDisplayName(customer);
  
  if (name === 'Guest') return 'G';
  
  // Handle email addresses
  if (name.includes('@')) {
    return name.charAt(0).toUpperCase();
  }
  
  // Handle multi-word names
  const words = name.split(' ').filter(word => word.length > 0);
  
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  
  // Take first letter of first two words
  return words.slice(0, 2)
              .map(word => word.charAt(0).toUpperCase())
              .join('');
};

/**
 * Check if customer data indicates a business account
 * @param {Object} customer - Customer object
 * @returns {boolean} True if customer appears to be a business
 */
export const isBusinessAccount = (customer) => {
  if (!customer) return false;
  
  const companyName = customer.customerCompany || 
                     customer.customer_company || 
                     customer.company;
                     
  return !!(companyName && companyName.trim());
};

/**
 * Get customer type for categorization
 * @param {Object} customer - Customer object
 * @returns {string} 'business', 'individual', or 'guest'
 */
export const getCustomerType = (customer) => {
  if (!customer) return 'guest';
  
  if (isBusinessAccount(customer)) {
    return 'business';
  }
  
  const firstName = customer.customerFirstName || 
                   customer.customer_first_name || 
                   customer.firstName || 
                   customer.first_name;
  const lastName = customer.customerLastName || 
                  customer.customer_last_name || 
                  customer.lastName || 
                  customer.last_name;
                  
  if (firstName || lastName) {
    return 'individual';
  }
  
  return 'guest';
};

/**
 * Get customer ID with fallback to display format
 * @param {Object} customer - Customer object
 * @returns {string} Customer ID or formatted fallback
 */
export const getCustomerIdDisplay = (customer) => {
  if (!customer) return '';
  
  // Try all possible ID fields
  const id = customer.id || 
             customer.customerId || 
             customer.customer_id || 
             customer.publicId || 
             customer.public_id;
             
  if (id) {
    return `Customer #${id}`;
  }
  
  return '';
};

/**
 * Get the primary customer ID from a customer object
 * Handles all possible ID field variations
 * @param {Object} customer - Customer object
 * @returns {string|null} The customer ID or null
 */
export const getCustomerId = (customer) => {
  if (!customer) return null;
  
  return customer.id || 
         customer.customerId || 
         customer.customer_id || 
         customer.publicId || 
         customer.public_id || 
         null;
};

/**
 * Create customer search string for filtering
 * Includes all possible search terms for comprehensive filtering
 * @param {Object} customer - Customer object
 * @param {Object} customersMap - Optional customers lookup map
 * @returns {string} Searchable string containing all customer identifiers
 */
export const getCustomerSearchString = (customer, customersMap = null) => {
  if (!customer) return 'guest';
  
  const searchTerms = [];
  
  // Helper to extract search terms from any data object
  const extractSearchTerms = (data) => {
    if (!data) return [];
    
    const terms = [];
    
    // Company names
    const companyName = data.customerCompany || data.customer_company || data.company;
    if (companyName) terms.push(companyName);
    
    // Individual names
    const firstName = data.customerFirstName || data.customer_first_name || data.firstName || data.first_name;
    const lastName = data.customerLastName || data.customer_last_name || data.lastName || data.last_name;
    if (firstName) terms.push(firstName);
    if (lastName) terms.push(lastName);
    
    // Emails
    const email = data.email || data.customerEmail;
    if (email) terms.push(email);
    
    return terms;
  };
  
  // Extract from primary data
  searchTerms.push(...extractSearchTerms(customer));
  
  // Extract from customers map if available
  if (customersMap && customer.customerId && customersMap[customer.customerId]) {
    searchTerms.push(...extractSearchTerms(customersMap[customer.customerId]));
  }
  
  // Add customer ID variations (all possible formats)
  if (customer.customerId) searchTerms.push(customer.customerId);
  if (customer.customer_id) searchTerms.push(customer.customer_id);
  // Note: customer._id removed as MongoDB legacy pattern
  if (customer.id) searchTerms.push(customer.id);
  if (customer.publicId) searchTerms.push(customer.publicId);
  if (customer.public_id) searchTerms.push(customer.public_id);
  
  return [...new Set(searchTerms)].join(' ').toLowerCase(); // Remove duplicates
};